import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, ArrowLeft, ArrowRight, Image as ImageIcon, Layers, ChevronDown, ChevronLeft, ChevronRight, GripVertical, Stamp, Trash2, Upload, Move, Eye, Sparkles, Loader2, Send, SlidersHorizontal } from 'lucide-react';
import { AIContentDisclosure, getDownloadLabel } from '@rehub/ai-transparency';
import type { AIContentClassification } from '@rehub/ai-transparency';
import { WatermarkSettings } from '../types';
import Viewer360 from './Viewer360';
import CurvesEditor from './CurvesEditor';
import { CurvesState, applyCurvesToImageUrlAsBlob } from '../services/curves';
import { supabase } from '../services/supabase';
import {
  BrandProfilePicker,
  MediaBrandManager,
  fetchBrandProfiles,
  createBrandProfile,
  updateBrandProfile,
  deleteBrandProfile,
  uploadBrandProfileLogo,
} from '@rehub/branded-viewer';
import type { MediaBrandProfile } from '@rehub/branded-viewer';

interface ResultViewerProps {
  originalUrl: string;
  processedUrl: string;
  onBack: () => void;
  onDownload: (format: 'png' | 'jpeg', watermark?: WatermarkSettings | null, sourceUrl?: string, aiDisclaimer?: boolean) => void;
  onAccept: (imageUrlToUse?: string) => void;
  userId?: string | null;
  onSendToHyperdusk?: () => void;
  stagingImageId?: string | null;
  onAnimateStaging?: () => void;
  /** Hand the staged 360° panorama to the 360 Tours app. Only set when result is 2:1. */
  onSendTo360Tours?: () => void;
  onSendToPhotoplan?: () => void;
  sendToPhotoplanLabel?: string;
  /**
   * Apply a free-text "request changes" refinement to the current 360 staged
   * panorama (e.g. "move the bed to the opposite wall"). Only wired by the
   * parent when the result is a 2:1 equirectangular image — the parent is
   * responsible for running the Gemini refinement pass and updating
   * `processedUrl` when it returns. Resolves on completion (success or
   * failure) so the UI can re-enable the input.
   */
  onRequestChanges?: (text: string) => Promise<void>;
  aiOperation?: string;
  aiClassification?: AIContentClassification;
}

const ResultViewer: React.FC<ResultViewerProps> = ({ 
  originalUrl, 
  processedUrl, 
  onBack, 
  onDownload,
  onAccept,
  userId,
  onSendToHyperdusk,
  stagingImageId,
  onAnimateStaging,
  onSendTo360Tours,
  onSendToPhotoplan,
  sendToPhotoplanLabel = 'Send to Photoplan',
  onRequestChanges,
  aiOperation = 'virtual_staging',
  aiClassification = 'AI_MODIFIED',
}) => {
  const downloadLabel = getDownloadLabel(aiClassification);
  const downloadCheckboxLabel =
    aiClassification === 'AI_MODIFIED'
      ? 'Add "AI Modified" label'
      : aiClassification === 'AI_GENERATED'
        ? 'Add "AI Generated" label'
        : 'Add "AI Enhanced" label';
  const [stagingCreditCost, setStagingCreditCost] = useState<number | null>(null);
  const [existingAnimation, setExistingAnimation] = useState<{ id: string; output_url: string | null; thumbnail_url: string | null } | null>(null);
  const [viewMode, setViewMode] = useState<'compare' | 'processed' | 'original'>('compare');
  const [sliderPosition, setSliderPosition] = useState(50);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [includeAiDisclaimer, setIncludeAiDisclaimer] = useState(false);
  
  // Watermark State
  const [isWatermarking, setIsWatermarking] = useState(false);
  const [watermark, setWatermark] = useState<WatermarkSettings | null>(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);

  // Curves editor state. Applying curves bakes the result into an in-memory
  // blob URL (adjustedUrl) used for preview, download and continue editing —
  // it is not persisted to the DB until the user downloads / continues.
  const [isCurvesOpen, setIsCurvesOpen] = useState(false);
  const [isSavingCurves, setIsSavingCurves] = useState(false);
  const [adjustedUrl, setAdjustedUrl] = useState<string | null>(null);

  /** Current result image to show / export (curves-adjusted or raw processed). */
  const displayUrl = adjustedUrl ?? processedUrl;

  const handleSaveCurves = async (curves: CurvesState) => {
    if (!displayUrl) return;
    setIsSavingCurves(true);
    try {
      const blob = await applyCurvesToImageUrlAsBlob(displayUrl, curves, 'image/jpeg', 0.95);
      const url = URL.createObjectURL(blob);
      setAdjustedUrl(url);
      setIsCurvesOpen(false);
    } catch (e) {
      console.error('Apply curves failed', e);
      alert('Failed to apply curves. Please try again.');
    } finally {
      setIsSavingCurves(false);
    }
  };
  
  // 360 Viewer State
  const [is360Image, setIs360Image] = useState(false);
  const [show360Viewer, setShow360Viewer] = useState(false);
  const [viewer360Mode, setViewer360Mode] = useState<'original' | 'processed'>('processed');

  // 360° "Request changes" panel — free-text refinement of the staged result.
  // Wired only when the parent passes `onRequestChanges`; collapsed by default
  // so it doesn't compete with the primary Continue Editing / Download actions.
  const [showChangesPanel, setShowChangesPanel] = useState(false);
  const [changesText, setChangesText] = useState('');
  const [isApplyingChanges, setIsApplyingChanges] = useState(false);
  const [changesError, setChangesError] = useState<string | null>(null);

  const handleApplyChanges = async () => {
    const text = changesText.trim();
    if (!text || !onRequestChanges || isApplyingChanges) return;
    setChangesError(null);
    setIsApplyingChanges(true);
    try {
      await onRequestChanges(text);
      // On success the parent updates processedUrl; clear the input so the
      // next refinement starts blank. Leave the panel open so the user can
      // chain refinements without an extra click.
      setChangesText('');
    } catch (err) {
      setChangesError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsApplyingChanges(false);
    }
  };

  // Brand Profiles State
  const [brands, setBrands] = useState<MediaBrandProfile[]>([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [showBrandManager, setShowBrandManager] = useState(false);
  const brandsFetched = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const downloadMenuRef = useRef<HTMLDivElement>(null);
  const processedImageRef = useRef<HTMLImageElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const loadBrands = useCallback(async () => {
    if (!userId) return [];
    setBrandsLoading(true);
    try {
      const data = await fetchBrandProfiles(supabase, userId);
      setBrands(data);
      return data;
    } finally {
      setBrandsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId && isWatermarking && !brandsFetched.current) {
      brandsFetched.current = true;
      loadBrands();
    }
  }, [userId, isWatermarking, loadBrands]);

  const handleBrandSelect = useCallback((brand: MediaBrandProfile) => {
    if (brand.logo_url) {
      setWatermark({
        imageUrl: brand.logo_url,
        x: brand.logo_position === 'top-right' ? 0.75 : 0.05,
        y: 0.05,
        scale: 0.2,
        opacity: 0.8,
      });
      setViewMode('processed');
    }
  }, []);

  // Direct download function (local implementation as backup)
  const handleDirectDownload = (format: 'png' | 'jpeg') => {
    console.log('handleDirectDownload called:', format);
    
    if (!displayUrl) {
      console.error('No image available to download');
      alert('No image available to download');
      return;
    }

    try {
      // Try calling the passed onDownload prop first (pass displayUrl so parent
      // uses the curves-adjusted image when one has been applied)
      if (onDownload) {
        console.log('Calling onDownload prop...');
        onDownload(format, watermark, displayUrl, includeAiDisclaimer);
      } else {
        // Fallback: direct download
        console.log('onDownload prop not available, using fallback');
        const link = document.createElement('a');
        link.href = displayUrl;
        link.download = `Staging-${Date.now()}.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Download error:', error);
      // Ultimate fallback - open in new tab
      window.open(displayUrl, '_blank');
    }
    
    setShowDownloadMenu(false);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSliderPosition(parseInt(e.target.value));
  };

  useEffect(() => {
    if (!onAnimateStaging) return;
    supabase
      .from('transition_animation_settings')
      .select('credit_cost, enabled')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => {
        if (data && data.enabled !== false) setStagingCreditCost(Number(data.credit_cost));
      });
  }, [onAnimateStaging]);

  useEffect(() => {
    if (!stagingImageId) return;
    supabase
      .from('transition_animations')
      .select('id, output_url, thumbnail_url, status')
      .eq('source_image_id', stagingImageId)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.output_url) setExistingAnimation(data);
      });
  }, [stagingImageId]);

  // Keyboard navigation for slider
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode === 'compare') {
        if (e.key === 'ArrowLeft') {
          setSliderPosition(prev => Math.max(0, prev - 5));
        } else if (e.key === 'ArrowRight') {
          setSliderPosition(prev => Math.min(100, prev + 5));
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode]);

  // Detect if image is 360 equirectangular
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      const ratio = img.width / img.height;
      const is360 = Math.abs(ratio - 2.0) < 0.1;
      setIs360Image(is360);
    };
    img.src = displayUrl;
  }, [displayUrl]);

  // Click outside to close download menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target as Node)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- Watermark Logic ---
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const url = URL.createObjectURL(file);
      setWatermark({
        imageUrl: url,
        x: 0.5, // Center
        y: 0.5,
        scale: 0.2, // 20% width
        opacity: 0.8
      });
      setViewMode('processed'); // Switch to view where watermark is visible
    }
  };

  const startDrag = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDraggingLogo(true);
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent | TouchEvent) => {
      if (!isDraggingLogo || !processedImageRef.current || !watermark) return;

      const rect = processedImageRef.current.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;

      // Calculate relative position (0-1)
      let x = (clientX - rect.left) / rect.width;
      let y = (clientY - rect.top) / rect.height;

      // Clamp
      x = Math.max(0, Math.min(1, x));
      y = Math.max(0, Math.min(1, y));

      setWatermark(prev => prev ? { ...prev, x, y } : null);
    };

    const handleUp = () => {
      setIsDraggingLogo(false);
    };

    if (isDraggingLogo) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('mouseup', handleUp);
      window.addEventListener('touchend', handleUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('touchmove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      window.removeEventListener('touchend', handleUp);
    };
  }, [isDraggingLogo, watermark]);

  return (
    <div className="flex flex-col h-full bg-black animate-fade-in">
       {/* Toolbar - Desktop */}
       <div className="hidden sm:flex h-16 bg-slate-900 border-b border-slate-800 items-center justify-between px-6 shrink-0 shadow-sm z-40 relative">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-slate-300 hover:text-purple-300 font-medium transition-colors"
            >
              <ArrowLeft size={18} />
              Back to Editor
            </button>
            {is360Image && (
              <span
                className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold tracking-wide uppercase bg-purple-600/20 text-purple-200 border border-purple-500/40"
                title="2:1 equirectangular panorama with GPano metadata preserved"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-purple-300 animate-pulse"></span>
                360° Panorama
              </span>
            )}
          </div>

          <div className="flex bg-slate-800 p-1 rounded-lg">
             <button
               onClick={() => setViewMode('original')}
               className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'original' ? 'bg-purple-600 shadow-md shadow-purple-500/25 text-white' : 'text-slate-400 hover:text-slate-200'}`}
             >
               Original
             </button>
             <button
               onClick={() => setViewMode('compare')}
               className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'compare' ? 'bg-purple-600 shadow-md shadow-purple-500/25 text-white' : 'text-slate-400 hover:text-slate-200'}`}
             >
               Before / After
             </button>
             <button
               onClick={() => setViewMode('processed')}
               className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all ${viewMode === 'processed' ? 'bg-purple-600 shadow-md shadow-purple-500/25 text-white' : 'text-slate-400 hover:text-slate-200'}`}
             >
               Result
             </button>
          </div>

          <div className="flex items-center gap-3">
            <AIContentDisclosure
              classification={aiClassification}
              operation={aiOperation}
              app="staging"
            />
            {is360Image && (
              <button
                onClick={() => { setViewer360Mode('processed'); setShow360Viewer(true); }}
                className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-blue-600 hover:bg-blue-500 text-white border border-blue-400/30"
              >
                <Eye size={18} />
                View 360°
              </button>
            )}
            
            <button
              onClick={() => setIsWatermarking(!isWatermarking)}
              className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${isWatermarking ? 'bg-amber-600 text-white border border-amber-500 shadow-md shadow-amber-500/25' : 'bg-amber-500/15 text-amber-300 border border-amber-500/40 hover:bg-amber-500/25'}`}
            >
              <Stamp size={18} />
              Watermark
            </button>

            <button
              onClick={() => setIsCurvesOpen(true)}
              className="px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all bg-sky-500/15 text-sky-300 border border-sky-500/40 hover:bg-sky-500/25"
              title="Curves"
            >
              <SlidersHorizontal size={18} />
              Curves
            </button>

            {is360Image && onRequestChanges && (
              <button
                onClick={() => setShowChangesPanel((v) => !v)}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all border ${
                  showChangesPanel
                    ? 'bg-purple-700/30 text-purple-200 border-purple-500/60'
                    : 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700 hover:border-purple-500/40 hover:text-purple-200'
                }`}
                title="Type a change you want applied to this 360° staging — uses the next generation in your bundle"
              >
                <Sparkles size={18} />
                Request changes
              </button>
            )}

            <button
              onClick={() => onAccept(displayUrl)}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-5 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md shadow-indigo-500/25 hover:shadow-lg"
              title="Keep this edit and make more changes"
            >
              <Layers size={18} />
              Continue Editing
            </button>

            {onSendToPhotoplan && (
              <button
                type="button"
                onClick={onSendToPhotoplan}
                className="px-4 py-2 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold"
                title={sendToPhotoplanLabel}
              >
                {sendToPhotoplanLabel}
              </button>
            )}

            {onSendToHyperdusk && !is360Image && (
              <button
                onClick={onSendToHyperdusk}
                className="group relative px-3 py-2 rounded-lg flex items-center transition-all whitespace-nowrap shrink-0 bg-black hover:bg-slate-950 border border-indigo-500/40 hover:border-indigo-400 shadow-[0_0_12px_-2px_rgba(99,102,241,0.5)] hover:shadow-[0_0_18px_-2px_rgba(99,102,241,0.8)]"
                title="Send to Hyper Dusk to animate this staged image"
              >
                <img
                  src="/hyperdusk-logo-transparent.png"
                  alt="Hyper Dusk"
                  className="h-6 w-auto object-contain"
                />
              </button>
            )}

            <div className="relative" ref={downloadMenuRef}>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                className="px-5 py-2 rounded-lg font-medium flex items-center gap-2 transition-all shadow-md shadow-purple-500/25 bg-purple-600 hover:bg-purple-500 text-white hover:shadow-lg"
              >
                <Download size={18} />
                Download
                <ChevronDown size={14} className={`transition-transform duration-200 ${showDownloadMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showDownloadMenu && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-900 rounded-lg shadow-xl border border-slate-700 py-2 z-50 animate-in fade-in zoom-in-95 duration-100 origin-top-right">
                   <label
                     className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 cursor-pointer border-b border-slate-800 mb-1"
                     onMouseDown={(e) => e.stopPropagation()}
                   >
                     <input
                       type="checkbox"
                       checked={includeAiDisclaimer}
                       onChange={(e) => setIncludeAiDisclaimer(e.target.checked)}
                       className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                     />
                     <span>{downloadCheckboxLabel}</span>
                   </label>
                   {/* Standard Downloads */}
                   <div className="px-4 py-1.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Download Format
                   </div>
                   <button 
                     type="button"
                     onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('jpeg'); }}
                     className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
                   >
                     <span>JPEG Image</span>
                     <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700 group-hover:bg-slate-700">Smaller</span>
                   </button>
                   <button 
                     type="button"
                     onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('png'); }}
                     className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors flex items-center justify-between group cursor-pointer"
                   >
                     <span>PNG Image</span>
                     <span className="text-[10px] bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded border border-slate-700 group-hover:bg-slate-700">Lossless</span>
                   </button>

                </div>
              )}
            </div>
          </div>
       </div>

       {/* 360 Tours hand-off announcement.
           Show whenever parent provides the hand-off callback; this avoids
           hiding the CTA if local ratio probing fails transiently. */}
       {onSendTo360Tours && (
         <button
           onClick={onSendTo360Tours}
           className="group hidden sm:flex w-full items-center gap-3 px-6 py-3 border-b border-[#8A2BE2]/30 bg-gradient-to-r from-[#8A2BE2]/15 via-[#8A2BE2]/5 to-transparent hover:from-[#8A2BE2]/20 hover:to-[#8A2BE2]/5 transition-all text-left"
           title="Send this staged 360° panorama to 360 Tours"
         >
           <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#8A2BE2]/25 border border-[#A855F7]/50 flex items-center justify-center">
             <img
               src="https://360tours.rehub.studio/360tours-studio-logo-transparent.png"
               alt="360 Tours"
               className="w-6 h-6 object-contain"
               onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
             />
           </div>
           <div className="flex-1 min-w-0">
             <div className="flex items-center gap-2 mb-0.5">
               <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#8A2BE2] text-white text-[10px] font-bold uppercase tracking-wider">
                 New
               </span>
               <span className="text-slate-100 font-semibold text-sm">
                 Bring this 360° straight into a tour
               </span>
             </div>
             <p className="text-slate-400 text-xs">
               Open this staged panorama in 360 Tours and turn it into a published, embeddable virtual tour.
             </p>
           </div>
           <span className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#8A2BE2] to-[#9B3CF3] group-hover:from-[#9B3CF3] group-hover:to-[#A855F7] text-white text-sm font-semibold transition-all shadow-lg shadow-[#8A2BE2]/30">
             Open in 360 Tours
             <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
           </span>
         </button>
       )}

       {onAnimateStaging && !is360Image && (
         <div className="w-full border-b border-blue-500/30 bg-gradient-to-r from-blue-600/15 to-transparent px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
           <div className="flex-1 min-w-0">
             <p className="text-slate-100 font-semibold text-sm">Animate this staging</p>
             <p className="text-slate-400 text-xs">Bring your staged room to life with ImageMotion.</p>
             {stagingCreditCost != null && (
               <p className="text-slate-300 text-xs mt-1">Create Staging Animation · {stagingCreditCost} credits</p>
             )}
           </div>
           <button
             type="button"
             onClick={onAnimateStaging}
             aria-label="Animate this staging"
             className="inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg bg-slate-950 hover:bg-slate-900 border border-white/10"
           >
             <img
               src="/staging-animation-logo.png"
               alt=""
               className="h-7 w-auto"
             />
             <ArrowRight size={16} className="text-white" />
           </button>
         </div>
       )}

       {existingAnimation?.output_url && (
         <div className="w-full border-b border-slate-800 px-4 sm:px-6 py-3 flex items-center gap-3">
           {existingAnimation.thumbnail_url && (
             <img src={existingAnimation.thumbnail_url} alt="" className="w-16 h-10 object-cover rounded" />
           )}
           <div className="flex-1">
             <p className="text-sm font-semibold text-white">Staging Animation</p>
           </div>
           <a href={existingAnimation.output_url} target="_blank" rel="noreferrer" className="text-xs text-blue-300">View Animation</a>
           <a href={existingAnimation.output_url} download className="text-xs text-blue-300">Download</a>
           <a href={`https://imagemotion.rehub.studio/staging-animation?id=${existingAnimation.id}`} target="_blank" rel="noreferrer" className="text-xs text-blue-300">Open in ImageMotion</a>
         </div>
       )}

       {/* 360 Tours hand-off — mobile compact variant */}
       {onSendTo360Tours && (
         <button
           onClick={onSendTo360Tours}
           className="sm:hidden w-full flex items-center gap-2 px-3 py-2.5 border-b border-[#8A2BE2]/30 bg-gradient-to-r from-[#8A2BE2]/20 to-transparent text-left"
           title="Send to 360 Tours"
         >
           <div className="flex-shrink-0 w-8 h-8 rounded-md bg-[#8A2BE2]/25 border border-[#A855F7]/50 flex items-center justify-center">
             <img
               src="https://360tours.rehub.studio/360tours-studio-logo-transparent.png"
               alt="360 Tours"
               className="w-5 h-5 object-contain"
               onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
             />
           </div>
           <div className="flex-1 min-w-0">
             <div className="flex items-center gap-1.5">
               <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#8A2BE2] text-white text-[9px] font-bold uppercase">New</span>
               <span className="text-slate-100 font-semibold text-xs truncate">Open in 360 Tours</span>
             </div>
             <p className="text-slate-400 text-[10px] truncate">Turn this panorama into a published tour</p>
           </div>
           <ArrowRight size={14} className="text-[#C084FC] flex-shrink-0" />
         </button>
       )}

       {/* Toolbar - Mobile */}
       <div className="sm:hidden bg-slate-900 border-b border-slate-800 shrink-0 shadow-sm z-40">
          {/* Top row: Back + View Mode */}
          <div className="flex items-center justify-between px-3 py-2">
            <button 
              onClick={onBack}
              className="flex items-center gap-1.5 text-slate-300 hover:text-purple-300 text-sm font-medium transition-colors"
            >
              <ArrowLeft size={16} />
              <span>Back</span>
            </button>

            <div className="flex bg-slate-800 p-0.5 rounded-lg">
               <button
                 onClick={() => setViewMode('original')}
                 className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${viewMode === 'original' ? 'bg-purple-600 shadow text-white' : 'text-slate-400'}`}
               >
                 Original
               </button>
               <button
                 onClick={() => setViewMode('compare')}
                 className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${viewMode === 'compare' ? 'bg-purple-600 shadow text-white' : 'text-slate-400'}`}
               >
                 Compare
               </button>
               <button
                 onClick={() => setViewMode('processed')}
                 className={`px-2.5 py-1 text-xs font-medium rounded transition-all ${viewMode === 'processed' ? 'bg-purple-600 shadow text-white' : 'text-slate-400'}`}
               >
                 Result
               </button>
            </div>
          </div>
          <div className="flex items-center px-3 pb-2">
            <AIContentDisclosure
              classification={aiClassification}
              operation={aiOperation}
              app="staging"
            />
          </div>
       </div>

       {/* Mobile Bottom Action Bar */}
       <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-slate-900 border-t border-slate-800 px-3 py-3 z-50 flex items-center justify-between gap-2">
          <button
            onClick={() => setIsWatermarking(!isWatermarking)}
            className={`p-2.5 rounded-lg transition-all ${isWatermarking ? 'bg-amber-600 text-white border border-amber-500' : 'bg-amber-500/15 text-amber-300 border border-amber-500/40'}`}
          >
            <Stamp size={20} />
          </button>

          <button
            onClick={() => setIsCurvesOpen(true)}
            className="p-2.5 rounded-lg transition-all bg-sky-500/15 text-sky-300 border border-sky-500/40"
            title="Curves"
          >
            <SlidersHorizontal size={20} />
          </button>

          {is360Image && onRequestChanges && (
            <button
              onClick={() => setShowChangesPanel((v) => !v)}
              className={`p-2.5 rounded-lg transition-all ${showChangesPanel ? 'bg-purple-900/30 text-purple-300 border border-purple-700' : 'bg-slate-800 text-slate-400 border border-slate-700'}`}
              title="Request changes"
            >
              <Sparkles size={20} />
            </button>
          )}

          <button
            onClick={() => onAccept(displayUrl)}
            className="flex-1 bg-indigo-600 text-white py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 text-sm shadow-md shadow-indigo-500/20"
          >
            <Layers size={16} />
            Continue Editing
          </button>

          {onSendToPhotoplan && (
            <button
              type="button"
              onClick={onSendToPhotoplan}
              className="px-3 py-2.5 rounded-lg bg-sky-600 hover:bg-sky-500 text-white text-sm font-semibold"
              title={sendToPhotoplanLabel}
            >
              {sendToPhotoplanLabel}
            </button>
          )}

          {onSendToHyperdusk && !is360Image && (
            <button
              onClick={onSendToHyperdusk}
              className="px-2 py-2 rounded-lg bg-black border border-indigo-500/40 shadow-[0_0_10px_-2px_rgba(99,102,241,0.5)] flex items-center"
              title="Send to Hyper Dusk to animate this staged image"
            >
              <img src="/hyperdusk-logo-transparent.png" alt="Hyper Dusk" className="h-5 w-auto object-contain" />
            </button>
          )}

          <div className="relative" ref={downloadMenuRef}>
            <button
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              className="bg-purple-600 text-white py-2.5 px-4 rounded-lg font-medium flex items-center gap-2 text-sm shadow-md shadow-purple-500/25"
            >
              <Download size={16} />
              Download
            </button>
            
            {showDownloadMenu && (
              <div className="absolute right-0 bottom-full mb-2 w-56 bg-slate-900 rounded-lg shadow-xl border border-slate-700 py-2 z-50">
                 <label
                   className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 cursor-pointer border-b border-slate-800 mb-1"
                   onMouseDown={(e) => e.stopPropagation()}
                 >
                   <input
                     type="checkbox"
                     checked={includeAiDisclaimer}
                     onChange={(e) => setIncludeAiDisclaimer(e.target.checked)}
                     className="rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                   />
                   <span>{downloadCheckboxLabel}</span>
                 </label>
                 <button 
                   type="button"
                   onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('jpeg'); }}
                   className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
                 >
                   JPEG (Smaller)
                 </button>
                 <button 
                   type="button"
                   onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); handleDirectDownload('png'); }}
                   className="w-full text-left px-4 py-2.5 text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors cursor-pointer"
                 >
                   PNG (Lossless)
                 </button>
              </div>
            )}
          </div>
       </div>
       
       {/* "Request changes" panel — 360° refinement (desktop + mobile,
            same layout). Shown only when the parent provided a refinement
            handler AND the user expanded the panel via the toolbar button.
            Uses one generation from the current bundle, same as a fresh stage. */}
       {is360Image && onRequestChanges && showChangesPanel && (
         <div className="bg-slate-900 border-b border-purple-900/40 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-end gap-3 animate-slide-down">
           <div className="flex-1 min-w-0">
             <label
               htmlFor="request-changes-textarea"
               className="text-[11px] font-semibold text-purple-300 uppercase tracking-wider mb-1.5 flex items-center gap-1.5"
             >
               <Sparkles size={12} />
               Request changes to this 360° staging
             </label>
             <textarea
               id="request-changes-textarea"
               value={changesText}
               onChange={(e) => { setChangesText(e.target.value); if (changesError) setChangesError(null); }}
               disabled={isApplyingChanges}
               placeholder='e.g. "Move the bed to the opposite wall", "Make the rug darker and add a bedside lamp", "Remove the chair in the corner"'
               rows={2}
               maxLength={600}
               onKeyDown={(e) => {
                 // Cmd/Ctrl-Enter submits — matches chat-style UX.
                 if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                   e.preventDefault();
                   void handleApplyChanges();
                 }
               }}
               className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500 resize-none disabled:opacity-50"
             />
             {changesError ? (
               <p className="text-[11px] text-red-400 mt-1.5">{changesError}</p>
             ) : (
               <p className="text-[10px] text-slate-500 mt-1 leading-snug">
                 Only the change you describe is applied — everything else stays the same. Uses one generation from the current bundle.
                 <span className="hidden sm:inline"> Press <kbd className="px-1 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400 text-[9px]">⌘</kbd>+<kbd className="px-1 py-0.5 rounded bg-slate-800 border border-slate-700 text-slate-400 text-[9px]">Enter</kbd> to apply.</span>
               </p>
             )}
           </div>
           <button
             onClick={handleApplyChanges}
             disabled={isApplyingChanges || !changesText.trim()}
             className={`shrink-0 px-5 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 transition-all border-2 ${
               isApplyingChanges || !changesText.trim()
                 ? 'bg-slate-800 border-slate-700 text-slate-500 cursor-not-allowed'
                 : 'bg-gradient-to-r from-purple-600 to-violet-600 border-purple-500 text-white hover:from-purple-500 hover:to-violet-500 active:scale-[0.98] shadow-md hover:shadow-lg'
             }`}
           >
             {isApplyingChanges ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
             {isApplyingChanges ? 'Applying…' : 'Apply changes'}
           </button>
         </div>
       )}

       {/* Watermark Controls Panel - Desktop */}
       {isWatermarking && (
          <div className="hidden sm:flex bg-slate-900 border-b border-slate-800 px-6 py-3 items-center gap-6 animate-slide-down">
              <input 
                type="file" 
                ref={logoInputRef}
                accept="image/png, image/jpeg, image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              
              {!watermark ? (
                <div className="flex items-center gap-3">
                  {userId && (
                    <BrandProfilePicker
                      brands={brands}
                      loading={brandsLoading}
                      onSelect={handleBrandSelect}
                      onManage={() => setShowBrandManager(true)}
                      accentColor="#f97316"
                      compact
                    />
                  )}
                  <button 
                    onClick={() => logoInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-900/30 text-orange-400 rounded-lg hover:bg-orange-900/50 transition-colors text-sm font-medium border border-orange-800/50"
                  >
                    <Upload size={16} />
                    Upload Logo
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-6 w-full">
                  <div className="flex items-center gap-2">
                     <img src={watermark.imageUrl} alt="Logo" className="w-8 h-8 object-contain rounded bg-slate-800 border border-slate-700" />
                     <button 
                       onClick={() => setWatermark(null)}
                       className="text-red-400 hover:bg-red-900/30 p-1.5 rounded-md transition-colors"
                     >
                       <Trash2 size={16} />
                     </button>
                  </div>

                  <div className="h-8 w-px bg-slate-800"></div>

                  <div className="flex items-center gap-3">
                     <span className="text-xs font-medium text-slate-400">Opacity</span>
                     <input 
                       type="range" 
                       min="10" 
                       max="100" 
                       value={watermark.opacity * 100}
                       onChange={(e) => setWatermark({...watermark, opacity: parseInt(e.target.value) / 100})}
                       className="w-32 accent-orange-500"
                     />
                     <span className="text-xs w-8 text-right text-slate-300">{Math.round(watermark.opacity * 100)}%</span>
                  </div>

                  <div className="flex items-center gap-3">
                     <span className="text-xs font-medium text-slate-400">Size</span>
                     <input 
                       type="range" 
                       min="5" 
                       max="80" 
                       value={watermark.scale * 100}
                       onChange={(e) => setWatermark({...watermark, scale: parseInt(e.target.value) / 100})}
                       className="w-32 accent-orange-500"
                     />
                     <span className="text-xs w-8 text-right text-slate-300">{Math.round(watermark.scale * 100)}%</span>
                  </div>

                  <div className="ml-auto text-xs text-orange-400 bg-orange-900/20 px-3 py-1.5 rounded-full border border-orange-900/50 flex items-center gap-1.5">
                    <Move size={12} />
                    Drag logo on image to position
                  </div>
                </div>
              )}
          </div>
       )}

       {/* Watermark Controls Panel - Mobile */}
       {isWatermarking && (
          <div className="sm:hidden bg-slate-900 border-b border-slate-800 px-3 py-2 animate-slide-down">
              <input 
                type="file" 
                ref={logoInputRef}
                accept="image/png, image/jpeg, image/webp"
                className="hidden"
                onChange={handleLogoUpload}
              />
              
              {!watermark ? (
                <div className="space-y-2">
                  {userId && (
                    <BrandProfilePicker
                      brands={brands}
                      loading={brandsLoading}
                      onSelect={handleBrandSelect}
                      onManage={() => setShowBrandManager(true)}
                      accentColor="#f97316"
                    />
                  )}
                  <button 
                    onClick={() => logoInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 bg-orange-900/30 text-orange-400 rounded-lg text-sm font-medium border border-orange-800/50 w-full justify-center"
                  >
                    <Upload size={16} />
                    Upload Logo
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                       <img src={watermark.imageUrl} alt="Logo" className="w-8 h-8 object-contain rounded bg-slate-800 border border-slate-700" />
                       <span className="text-xs text-slate-400">Drag on image to move</span>
                    </div>
                    <button 
                      onClick={() => setWatermark(null)}
                      className="text-red-400 bg-red-900/20 p-1.5 rounded-md"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-1 flex items-center gap-2">
                       <span className="text-[10px] font-medium text-slate-500">Opacity</span>
                       <input 
                         type="range" 
                         min="10" 
                         max="100" 
                         value={watermark.opacity * 100}
                         onChange={(e) => setWatermark({...watermark, opacity: parseInt(e.target.value) / 100})}
                         className="flex-1 accent-orange-500"
                       />
                    </div>

                    <div className="flex-1 flex items-center gap-2">
                       <span className="text-[10px] font-medium text-slate-500">Size</span>
                       <input 
                         type="range" 
                         min="5" 
                         max="80" 
                         value={watermark.scale * 100}
                         onChange={(e) => setWatermark({...watermark, scale: parseInt(e.target.value) / 100})}
                         className="flex-1 accent-orange-500"
                       />
                    </div>
                  </div>
                </div>
              )}
          </div>
       )}

       {/* View Area */}
       <div className="flex-1 overflow-hidden p-4 sm:p-8 pb-20 sm:pb-8 flex items-center justify-center bg-black">
          <div className="relative shadow-2xl rounded-lg overflow-hidden bg-slate-900 ring-1 ring-white/10 w-fit h-fit flex justify-center">
            
            {viewMode === 'processed' && (
              <div className="relative group">
                <img 
                  ref={processedImageRef}
                  src={displayUrl} 
                  alt="Processed" 
                  className="max-w-full max-h-[80vh] object-contain block" 
                />
                {includeAiDisclaimer && (
                  <div className="absolute bottom-2 right-2 z-20 pointer-events-none rounded-full bg-black/45 px-2.5 py-1 text-[10px] sm:text-xs font-medium text-white/90 tracking-wide">
                    {downloadLabel}
                  </div>
                )}
                {watermark && (
                  <>
                    <div
                      className={`absolute select-none origin-center ${
                        isDraggingLogo 
                          ? 'cursor-grabbing z-50' 
                          : 'cursor-move z-40 hover:z-50'
                      }`}
                      style={{
                        left: `${watermark.x * 100}%`,
                        top: `${watermark.y * 100}%`,
                        width: `${watermark.scale * 100}%`,
                        transform: 'translate(0, 0)',
                        touchAction: 'none'
                      }}
                      onMouseDown={startDrag}
                      onTouchStart={startDrag}
                    >
                         {/* Visual wrapper for the image */}
                         <div className={`
                             w-full h-full relative transition-all duration-200
                             ${isDraggingLogo 
                               ? 'ring-2 ring-orange-500/80 shadow-[0_0_20px_rgba(249,115,22,0.3)] bg-orange-500/5 backdrop-blur-[1px] rounded-sm' 
                               : 'hover:ring-1 hover:ring-white/40 rounded-sm'
                             }
                         `}>
                             <img 
                               src={watermark.imageUrl}
                               alt="Watermark"
                               className="w-full h-full object-contain pointer-events-none"
                               style={{ opacity: watermark.opacity }}
                             />
                             
                             {/* Corner markers for drag indication */}
                             {isDraggingLogo && (
                                <>
                                    <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                    <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-orange-500 border border-white rounded-full shadow-sm"></div>
                                </>
                             )}
                         </div>
                    </div>

                    {/* Helper Tooltip when dragging */}
                    {isDraggingLogo && (
                        <div 
                            className="absolute pointer-events-none z-50 bg-slate-900/95 text-orange-200 text-[10px] font-medium px-2.5 py-1.5 rounded-md shadow-xl backdrop-blur-md border border-orange-500/30 flex items-center gap-2 transform -translate-x-1/2 -translate-y-full mt-[-8px]"
                            style={{
                                left: `${(watermark.x + watermark.scale/2) * 100}%`,
                                top: `${watermark.y * 100}%`,
                            }}
                        >
                            <Move size={10} className="text-orange-400" />
                            <span>
                                X: {Math.round(watermark.x * 100)}% Y: {Math.round(watermark.y * 100)}%
                            </span>
                        </div>
                    )}

                    {/* Hover Hint when not dragging */}
                    {!isDraggingLogo && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none flex items-center gap-1.5 border border-white/10 z-30">
                            <Move size={12} />
                            Drag watermark to reposition
                        </div>
                    )}
                  </>
                )}
              </div>
            )}

            {viewMode === 'original' && (
               <img src={originalUrl} alt="Original" className="max-w-full max-h-[80vh] object-contain block" />
            )}

            {viewMode === 'compare' && (
              <div ref={containerRef} className="relative select-none group w-fit h-fit">
                 <img 
                    src={displayUrl} 
                    alt="After" 
                    className="max-w-full max-h-[80vh] object-contain block select-none pointer-events-none" 
                    draggable={false}
                 />

                 {/* Top Image: Original - Clipped */}
                 <img 
                    src={originalUrl} 
                    alt="Before" 
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none" 
                    style={{ 
                      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` 
                    }}
                 />

                 {/* Divider Line */}
                 <div 
                    className="absolute inset-y-0 w-0.5 bg-white shadow-[0_0_15px_rgba(0,0,0,0.5)] cursor-ew-resize z-20 pointer-events-none"
                    style={{ left: `${sliderPosition}%` }}
                 >
                    {/* Handle Button */}
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-400">
                      <ChevronLeft size={14} className="text-slate-400" />
                      <GripVertical size={14} className="text-slate-300 mx-[-2px]" />
                      <ChevronRight size={14} className="text-slate-400" />
                    </div>
                 </div>

                 {/* Interactive Input Overlay */}
                 <input 
                   type="range"
                   min="0"
                   max="100"
                   value={sliderPosition}
                   onChange={handleSliderChange}
                   className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-30 touch-none"
                   aria-label="Comparison slider"
                 />
                 
                 {/* Floating Labels */}
                 <div className="absolute top-4 left-4 bg-black/60 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-md shadow-sm border border-white/10 pointer-events-none transition-opacity duration-300" style={{ opacity: sliderPosition < 10 ? 0 : 1 }}>
                    Original
                 </div>
                 <div className="absolute top-4 right-4 bg-purple-600/80 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur-md shadow-sm border border-white/10 pointer-events-none transition-opacity duration-300" style={{ opacity: sliderPosition > 90 ? 0 : 1 }}>
                    Staged
                 </div>
                 {includeAiDisclaimer && (
                   <div className="absolute bottom-2 right-2 z-20 pointer-events-none rounded-full bg-black/45 px-2.5 py-1 text-[10px] sm:text-xs font-medium text-white/90 tracking-wide">
                     {downloadLabel}
                   </div>
                 )}
              </div>
            )}
          </div>
       </div>
       
       {/* Curves Editor Modal */}
       {isCurvesOpen && displayUrl && (
         <CurvesEditor
           imageUrl={displayUrl}
           isSaving={isSavingCurves}
           onSave={handleSaveCurves}
           onCancel={() => !isSavingCurves && setIsCurvesOpen(false)}
         />
       )}

       {/* 360 Viewer Modal */}
       {show360Viewer && (
         <Viewer360 
           imageUrl={viewer360Mode === 'original' ? originalUrl : displayUrl}
           onClose={() => setShow360Viewer(false)}
           title={viewer360Mode === 'original' ? '360° Original' : '360° Result'}
         />
       )}

       {/* Brand Manager Modal */}
       {showBrandManager && userId && (
         <MediaBrandManager
           userId={userId}
           brands={brands}
           loading={brandsLoading}
           onClose={() => setShowBrandManager(false)}
           onLoadBrands={loadBrands}
           onCreateBrand={(input) => createBrandProfile(supabase, userId, input)}
           onUpdateBrand={(id, input) => updateBrandProfile(supabase, userId, id, input)}
           onDeleteBrand={(id) => deleteBrandProfile(supabase, userId, id)}
           onUploadLogo={(file) => uploadBrandProfileLogo(supabase, file, userId)}
           applyMode
           onBrandApplied={(brand) => { handleBrandSelect(brand); setShowBrandManager(false); }}
           accentColor="#f97316"
           mode="watermark"
         />
       )}
    </div>
  );
};

export default ResultViewer;
